import type { HoverHit, WorkerRequest, WorkerResponse } from './protocol'

export interface WorkerLike {
  postMessage(message: WorkerRequest): void
  terminate(): void
  onmessage: ((event: MessageEvent<WorkerResponse>) => void) | null
  onerror: ((event: ErrorEvent) => void) | null
}

export interface HoverDataListeners {
  onProgress?: (loadedBytes: number, totalBytes: number | null) => void
  onReady?: (featureCount: number) => void
  onError?: (message: string) => void
  onHit?: (hit: HoverHit | null) => void
}

interface Pointer {
  x: number
  y: number
  tolerance: number
}

/**
 * Main-thread side of the hover worker. Pointer moves are latest-wins: at most
 * one query is in flight, and moves that arrive meanwhile collapse into the
 * newest one, so a fast mouse never builds a queue behind a slow frame.
 */
export class HoverDataClient {
  private nextRequestId = 0
  private inFlight: number | null = null
  private pending: Pointer | null = null
  private ready = false

  constructor(
    private readonly worker: WorkerLike,
    private readonly listeners: HoverDataListeners,
  ) {
    worker.onmessage = (event) => this.handle(event.data)
    worker.onerror = (event) =>
      listeners.onError?.(event.message || 'Worker failed')
  }

  load(url: string, uncompressedSize: number | null): void {
    this.worker.postMessage({ type: 'load', url, uncompressedSize })
  }

  setPointer(x: number, y: number, tolerance: number): void {
    this.pending = { x, y, tolerance }
    this.flush()
  }

  clearPointer(): void {
    this.pending = null
    // Drop whatever is in flight so a late answer can't re-show the popup.
    this.inFlight = null
    this.listeners.onHit?.(null)
  }

  terminate(): void {
    this.worker.onmessage = null
    this.worker.onerror = null
    this.worker.terminate()
  }

  private flush(): void {
    if (!this.ready || this.inFlight !== null || !this.pending) return
    const { x, y, tolerance } = this.pending
    this.pending = null
    this.inFlight = this.nextRequestId++
    this.worker.postMessage({
      type: 'query',
      requestId: this.inFlight,
      x,
      y,
      tolerance,
    })
  }

  private handle(message: WorkerResponse): void {
    switch (message.type) {
      case 'progress':
        this.listeners.onProgress?.(message.loadedBytes, message.totalBytes)
        break
      case 'ready':
        this.ready = true
        this.listeners.onReady?.(message.featureCount)
        this.flush()
        break
      case 'error':
        this.listeners.onError?.(message.message)
        break
      case 'result':
        if (message.requestId !== this.inFlight) break
        this.inFlight = null
        // A newer position is waiting; its answer supersedes this one.
        if (this.pending) {
          this.flush()
        } else {
          this.listeners.onHit?.(message.hit)
        }
        break
    }
  }
}
